import { globalConfig as getGlobalConfig } from 'lib_ui-services';

export const SETTING_NAME = 'batchedTransactionFanOut';

export const _private = { getGlobalConfig };

/**
 * Whether the SERVER applies a `{relation}-transaction`'s change-set to its entries, so the client
 * does not have to send one PATCH per entry (GH #1521).
 *
 * This has to be gated, and gated on something the SERVER tells us, because of version skew. The
 * client half and the server half are independent deployables — an APK ships on its own cadence —
 * and the two failure modes are not symmetric:
 *
 * - New client, new server: one request, the server fans out. What we want.
 * - New client, OLD server: the server ignores `changes` and applies nothing. If the client had
 *   already stopped sending its own PATCHes, a mass update would silently do **nothing at all**
 *   while still reporting success. Total, silent data loss.
 *
 * A tenant setting is exactly the right shape for that, because it arrives FROM the server: an old
 * server has never heard of it, so it is absent, so this returns false, so the client keeps fanning
 * out itself exactly as it always has. The unsafe combination is unreachable rather than merely
 * unlikely. It doubles as an ops kill switch if the server-side fan-out misbehaves — worth having
 * for a change on the write path of every mass update.
 *
 * Read from tenant settings only, deliberately: a build-time feature flag (`GLOBAL_FEATURE_FLAGS`)
 * is baked into the client and says nothing about what the server it is talking to can do, which is
 * the only question being asked here.
 *
 * @returns {boolean}
 */
export default function isBatchedTransactionFanOutEnabled() {
    const { tenantSettings } = _private.getGlobalConfig() || {};
    return tenantSettings?.[SETTING_NAME] === true;
}
